/**
 * Property Links Menu - External links dropdown for property
 * Shows Airbnb, VRBO, CLC Website, and other external links for the selected property
 */
import { useState, useEffect, useRef } from 'react';
import { useTheme } from '../../contexts/ThemeContext.jsx';
import { useSession } from '../../contexts/SessionContext.jsx';
import Popover from '../shared/Popover.jsx';
import LinksReportsSocialIcons from './LinksReportsSocialIcons.jsx';
import clcLogo from '../../assets/brands/clc.webp';
import airbnbLogo from '../../assets/brands/channels/airbnb.svg';
import vrboLogo from '../../assets/brands/channels/vrbo.svg';
import expediaLogo from '../../assets/brands/channels/expedia.svg';
import googleMapsLogo from '../../assets/brands/google-maps.svg';

export default function PropertyLinksMenu({ property }) {
  const { theme } = useTheme();
  const { selectedProperty } = useSession();
  const [isOpen, setIsOpen] = useState(false);
  const menuItemsRef = useRef([]);

  // Get external links from property (with fallback to selectedProperty from session)
  const propertyData = property || selectedProperty;
  const externalLinks = propertyData?.external_links || {};

  // Define link items with brand logos and labels
  // Order: CLC first (if present), then Airbnb, VRBO, Expedia, Maps
  // Only include links that are actually available in external_links
  // All logos are imported to ensure cache-busting via Vite bundler hashing
  const linkItems = [
    {
      id: 'clc',
      label: 'CLC',
      logo: clcLogo, // Imported asset gets hashed filename for cache-busting
      url: externalLinks.clc,
      show: !!externalLinks.clc,
    },
    {
      id: 'airbnb',
      label: 'Airbnb',
      logo: airbnbLogo, // Airbnb Bélo mark (channels/)
      url: externalLinks.airbnb,
      show: !!externalLinks.airbnb,
    },
    {
      id: 'vrbo',
      label: 'VRBO',
      logo: vrboLogo, // VRBO shield/house mark (channels/)
      url: externalLinks.vrbo,
      show: !!externalLinks.vrbo,
    },
    {
      id: 'expedia',
      label: 'Expedia',
      logo: expediaLogo, // Expedia E mark (channels/)
      url: externalLinks.expedia,
      show: !!externalLinks.expedia,
    },
    {
      id: 'maps',
      label: 'Google Maps',
      logo: googleMapsLogo, // Official Google Maps logo from SimpleIcons
      url: externalLinks.maps,
      show: !!externalLinks.maps,
    },
  ].filter(item => item.show);

  // Focus first item when menu opens
  useEffect(() => {
    if (isOpen && menuItemsRef.current.length > 0) {
      setTimeout(() => {
        const firstClickable = menuItemsRef.current.find(el => el);
        firstClickable?.focus();
      }, 0);
    }
  }, [isOpen]);

  const handleLinkClick = (url) => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
    setIsOpen(false);
  };

  // Fixed 24x24 icon container for consistent alignment (retina-safe)
  const iconContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 24,
    height: 24,
    flexShrink: 0,
  };
  const iconImgStyle = {
    width: 24,
    height: 24,
    objectFit: 'contain',
  };

  // Don't render if no links available
  if (linkItems.length === 0) {
    return null;
  }

  return (
    <Popover
      trigger={({ ref, ...props }) => (
        <button
          ref={ref}
          onClick={() => setIsOpen(!isOpen)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setIsOpen(!isOpen);
            }
          }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 'var(--spacing-2)',
            fontSize: 'var(--font-size-base)',
            color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
            backgroundColor: 'transparent',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            cursor: 'pointer',
            transition: 'var(--transition-base)',
            width: '36px',
            height: '36px',
            minWidth: '36px',
            minHeight: '36px',
            opacity: 0.7,
          }}
          title="Property links"
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = theme === 'dark' ? 'var(--color-bg-hover-dark)' : 'var(--color-bg-hover)';
            e.currentTarget.style.opacity = '1';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.opacity = '0.7';
          }}
          onFocus={(e) => {
            e.currentTarget.style.outline = '2px solid var(--color-brand-primary)';
            e.currentTarget.style.outlineOffset = '2px';
          }}
          onBlur={(e) => {
            e.currentTarget.style.outline = 'none';
          }}
          aria-label="Property links"
          aria-haspopup="menu"
          aria-expanded={isOpen}
          {...props}
        >
          <span style={{ fontSize: 'var(--font-size-lg)', lineHeight: 1 }}>🔗</span>
        </button>
      )}
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      placement="bottom-end"
    >
      <div
        style={{
          minWidth: '200px',
          borderRadius: 'var(--radius-md)',
          border: `1px solid ${theme === 'dark' ? 'var(--color-border-medium-dark)' : 'var(--color-border-medium)'}`,
          backgroundColor: theme === 'dark' ? 'var(--color-bg-primary-dark)' : 'var(--color-bg-primary)',
          boxShadow: theme === 'dark'
            ? '0 4px 6px rgba(0, 0, 0, 0.3)'
            : '0 4px 6px rgba(0, 0, 0, 0.1)',
          overflow: 'hidden',
          padding: 'var(--spacing-1)',
        }}
        role="menu"
      >
        {linkItems.map((item, index) => (
          <button
            key={item.id}
            ref={(el) => {
              if (el) menuItemsRef.current[index] = el;
            }}
            onClick={() => handleLinkClick(item.url)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleLinkClick(item.url);
              } else if (e.key === 'ArrowDown') {
                e.preventDefault();
                const next = menuItemsRef.current[index + 1];
                if (next) next.focus();
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                const prev = menuItemsRef.current[index - 1];
                if (prev) prev.focus();
              } else if (e.key === 'Escape') {
                e.preventDefault();
                setIsOpen(false);
              }
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-3)',
              padding: 'var(--spacing-3) var(--spacing-4)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-medium)',
              textDecoration: 'none',
              transition: 'var(--transition-base)',
              cursor: 'pointer',
              background: 'none',
              border: 'none',
              width: '100%',
              textAlign: 'left',
              color: theme === 'dark' ? 'var(--color-text-primary-dark)' : 'var(--color-text-primary)',
              minHeight: '44px',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = theme === 'dark' ? 'var(--color-bg-hover-dark)' : 'var(--color-bg-hover)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
            onFocus={(e) => {
              e.currentTarget.style.outline = '2px solid var(--color-brand-primary)';
              e.currentTarget.style.outlineOffset = '2px';
            }}
            onBlur={(e) => {
              e.currentTarget.style.outline = 'none';
            }}
            role="menuitem"
          >
            <span style={iconContainerStyle}>
              <img 
                src={item.logo} 
                alt=""
                style={iconImgStyle}
                width={24}
                height={24}
                decoding="async"
              />
            </span>
            <span>{item.label}</span>
            <span style={{ marginLeft: 'auto', fontSize: 'var(--font-size-xs)', opacity: 0.6 }}>↗</span>
          </button>
        ))}
        <div
          style={{
            borderTop: `1px solid ${theme === 'dark' ? 'var(--color-border-medium-dark)' : 'var(--color-border-light)'}`,
            padding: 'var(--spacing-3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: 'var(--spacing-2)',
          }}
        >
          <LinksReportsSocialIcons />
        </div>
      </div>
    </Popover>
  );
}
